import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Tile from "../../../types/tile";
import CircleTile from "../../base/CircleTile";
import { Delete, Edit } from "@mui/icons-material";
import theme from "../../../theme";
import DashboardTile from "../../base/DashboardTile";
import { useDispatch, useSelector } from "../../../store";
import Button from "../../base/Button";
import { useNavigate } from "react-router-dom";
import CountTile from "../../base/CountTile";
import BarTile from "../../base/BarTile";
import ReportTile from "../../base/ReportTile";
import { TileTypes } from "../../../types/tileType";
import { deleteTile, selectTile } from "../../../slices/tile";
import {
  getDashboardDetail,
  setSelectedDashboard,
} from "../../../slices/dashboard";
import LineTile from "../../base/LineTile";
import DeleteConfirmation from "../../base/DeleteConfirmation";
import { useDataLoader } from "../../../hooks/useDataLoader";

interface Props {
  tile: Tile;
  detail?: boolean;
}

const TileBase: React.FC<Props> = (props) => {
  const { tile, detail } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDashboard } = useSelector((store) => store.dashboard);
  const { editMode, globalReload } = useSelector((store) => store.shared);
  const { status, tileLoaded } = useSelector((store) => store.tiles);

  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const md = useMediaQuery(theme.breakpoints.up("md"));
  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  const [dialogOpen, setDialogOpen] = useState<{
    id?: number;
    type: "delete" | "none";
  }>({ id: undefined, type: "none" });

  const [tileModel, setTileModel] = useState(tile);

  useDataLoader({
    tile,
    dispatch,
    detail,
    setTileModel,
    globalReload,
  });

  const getTile = (): { chart: JSX.Element; color?: string } => {
    switch (tileModel?.type?.id) {
      case TileTypes.Bar:
        return {
          chart: <BarTile tileModel={tileModel!} detail={detail} />,
          color: theme.palette.text.primary,
        };
      case TileTypes.Line:
        return {
          chart: <LineTile tileModel={tileModel!} detail={detail} />,
          color: theme.palette.text.primary,
        };
      case TileTypes.Circle:
        return {
          chart: <CircleTile tileModel={tileModel!} detail={detail} />,
          color: theme.palette.text.primary,
        };
      case TileTypes.Count:
        return {
          chart: <CountTile tileModel={tileModel!} detail={detail} />,
          color:
            tileModel.threshold! >= 0 &&
            +tileModel.value! > tileModel.threshold!
              ? theme.palette.error.main
              : theme.palette.success.main,
        };
      case TileTypes.Number:
        return {
          chart: <CountTile tileModel={tileModel!} detail={detail} />,
          color: tileModel?.backgroundColor ?? theme.palette.secondary.main,
        };
      case TileTypes.Report:
        return {
          chart: <ReportTile />,
          color: theme.palette.info.main,
        };
      default:
        return { chart: <></>, color: "" };
    }
  };

  const handleTileClick = () => {
    if (!editMode && !detail) {
      dispatch(selectTile(tileModel));
      navigate(`/tile/${tileModel?.id}`);
    }
  };

  const handleOpenDialog = (type: "delete" | "none") => {
    setDialogOpen({ id: tileModel?.id!, type: type });
  };

  const handleCloseDialog = () => {
    setDialogOpen({ id: undefined, type: "none" });
  };

  const handleDelete = async () => {
    await dispatch(deleteTile(dialogOpen.id!));
    await dispatch(getDashboardDetail(currentDashboard?.id!));
    handleCloseDialog();
  };

  return (
    <>
      {tileModel?.type?.id! > 0 ? (
        tileModel?.type?.id === TileTypes.Dashboard ? (
          <DashboardTile tile={tileModel} />
        ) : (
          <Box
            onClick={handleTileClick}
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center" // vertical center
            alignItems="center" // horizontal center
            textAlign="center"
            position="relative"
            sx={{
              backgroundColor: getTile()?.color,
              cursor: !editMode && !detail ? "pointer" : "",
            }}
            color="#fff"
          >
            <Box position="absolute" width="100%" height="100%" />
            <Box
              position="absolute"
              top={
                lg
                  ? "90px"
                  : md
                  ? detail
                    ? "20vh"
                    : "80px"
                  : sm
                  ? detail
                    ? "15vw"
                    : "60px"
                  : detail
                  ? "17vw"
                  : "50px"
              }
              bottom={"70px"}
              width="100%"
              height="100%"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                color="#fff"
                fontSize={
                  lg
                    ? detail
                      ? "4vh"
                      : "3vh"
                    : sm
                    ? detail
                      ? "8vw"
                      : "3vw"
                    : detail
                    ? "8vw"
                    : "3vw"
                }
                sx={{
                  width: "100%",
                  paddingX: "60px",
                }}
              >
                {tileModel?.title}
              </Typography>
              <Box
                position="relative"
                width="100%"
                height="100%"
                paddingBottom={md ? "190px" : "100px"}
              >
                {(!tileModel.value || tileModel.value === "") &&
                tileModel.data?.length! < 1 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ width: "100%", height: "100%" }}
                  >
                    {tileLoaded.find((x) => x.id === tile?.id)?.loaded ? (
                      <Typography
                        variant="h5"
                        sx={{
                          fontSize: "clamp(12px, 1.5vw, 18px)",
                        }}
                      >
                        No Data Available
                      </Typography>
                    ) : (
                      <CircularProgress />
                    )}
                  </Box>
                ) : (
                  getTile()?.chart
                )}
              </Box>
            </Box>
            {!!editMode && !!tileModel && (
              <Box width="100%" position="absolute" bottom={md ? 35 : 20}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="center"
                  color="#fff"
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      dispatch(setSelectedDashboard(tileModel?.dashboard?.id));
                      navigate(`/edit-tile/${tileModel?.id}`);
                    }}
                    disabled={false}
                    color="inherit"
                    tooltip="Edit Tile"
                    icon={<Edit fontSize="small" />}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleOpenDialog("delete");
                    }}
                    disabled={false}
                    color="inherit"
                    tooltip="Delete Tile"
                    icon={<Delete fontSize="small" />}
                  />
                </Box>
                <Dialog open={dialogOpen.type === "delete"}>
                  <DeleteConfirmation
                    name="this tile"
                    handleClose={handleCloseDialog}
                    handleDelete={handleDelete}
                    loading={status === "loading"}
                  />
                </Dialog>
              </Box>
            )}
          </Box>
        )
      ) : (
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center" // vertical center
          alignItems="center" // horizontal center
          textAlign="center"
          position="relative"
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default TileBase;
